@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_driver

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import org.example.project.settings.LumaticaInfoField
import org.example.project.dashboard.DashboardTheme
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.service.HofService
import org.example.project.service.YardApi
import org.example.project.service.ZustandManager
import org.example.project.setup.SetupNodeApi
import org.example.project.utils.All
import org.example.project.utils.LocalizationManager
import org.example.project.yard.yardPinCode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun FahrerLobbyWeHeader(entry: All) {
    val t: (String) -> String = { LocalizationManager.get(it) }
    var expanded by remember { mutableStateOf(false) }
    var lkwVorDir by remember { mutableStateOf("0") }
    var standortLabel by remember { mutableStateOf("-") }
    var lieferscheinnummern by remember { mutableStateOf(listOf(entry.lieferscheinnummer).filter { it.isNotBlank() }) }
    var nowTick by remember { mutableStateOf(currentClockTimeWithSeconds()) }

    val lobbyStandortId = fahrerLobbyStandortId(entry)
    val pin = yardPinCode(entry)
    val hasAssignedTor = entry.tor != null &&
        entry.zustand >= ZustandManager.TOR_ZUGEWIESEN &&
        entry.torSystemgesetztUm.isBlank()
    val torValue = if (hasAssignedTor) entry.tor?.toString().orEmpty() else "-"
    val statusLabel = remember(entry.zustand, entry.auftragsart) {
        fahrerStatusLabel(entry)
    }
    val vorgangsdauer = remember(
        entry.uhrzeitAnmeldung,
        entry.uhrzeitAbgeschlossen,
        nowTick
    ) {
        formatDurationWithSeconds(
            start = entry.uhrzeitAnmeldung,
            end = entry.uhrzeitAbgeschlossen.ifBlank { nowTick }
        )
    }

    LaunchedEffect(entry.lfdNr, entry.uhrzeitAnmeldung, entry.uhrzeitAbgeschlossen) {
        while (true) {
            nowTick = currentClockTimeWithSeconds()
            if (entry.uhrzeitAbgeschlossen.isNotBlank()) break
            delay(1000)
        }
    }

    LaunchedEffect(entry.lfdNr, entry.zustand, lobbyStandortId, entry.firmenId) {
        val layout = YardApi.loadStandortLayout(lobbyStandortId, entry.firmenId)
        standortLabel = runCatching {
            val standort = if (lobbyStandortId.isNotBlank()) {
                SetupNodeApi.loadStandorte(entry.firmenId).firstOrNull { it.id == lobbyStandortId }
            } else null
            buildStandortText(
                name = standort?.standort.orEmpty().ifBlank { entry.standort.ifBlank { entry.firma } },
                street = standort?.strasse.orEmpty(),
                houseNumber = standort?.hausnummer.orEmpty(),
                postalCode = standort?.plz.orEmpty(),
                city = standort?.ort.orEmpty()
            )
        }.getOrElse {
            buildStandortText(
                name = entry.standort.ifBlank { entry.firma },
                street = entry.strasse,
                houseNumber = entry.hausnummer,
                postalCode = entry.plz,
                city = entry.ort
            )
        }

        val standortListe = if (entry.auftragsart == SLOT_BEREICH_WE || entry.lieferscheinnummer.isBlank()) {
            YardApi.loadWeLkwListe(entry.firmenId)
        } else {
            YardApi.loadWaLkwListe(entry.firmenId)
        }
        val lobbyGruppe = lobbyAuftraege(entry, standortListe)
        HofService.bind(layout, lobbyGruppe)
        lieferscheinnummern = lobbyLieferscheinnummern(entry, lobbyGruppe)

        val eigenePrio = yardPrioValue(entry)
        lkwVorDir = if (eigenePrio == null) {
            "0"
        } else {
            standortListe
                .filter { sameLobbyStandort(it, lobbyStandortId, entry.standort) }
                .filter { !sameLobbyGroup(it, entry) }
                .filter { it.zustand < ZustandManager.IN_VERLADUNG }
                .count { kandidat ->
                    val kandidatPrio = yardPrioValue(kandidat)
                    kandidatPrio != null && kandidatPrio < eigenePrio
                }
                .toString()
        }
    }

    DriverInfoPanel(
        expanded = expanded,
        onToggle = { expanded = !expanded },
        rowOne = listOf(
            t("driver_lobby_process_duration") to vorgangsdauer,
            t("driver_lobby_status") to statusLabel,
            "PIN" to pin,
            t("driver_lobby_gate") to torValue,
            t("driver_lobby_trucks_ahead") to lkwVorDir
        ),
        rowTwo = lieferscheinnummern.mapIndexed { index, nummer ->
            val label = if (lieferscheinnummern.size > 1) {
                "${t("driver_lobby_delivery_note_number")} ${index + 1}"
            } else {
                t("driver_lobby_delivery_note_number")
            }
            label to nummer
        } + listOf(
            t("driver_lobby_pallets") to entry.palettenanzahl.toString(),
            t("driver_lobby_time_window") to entry.verladeuhrzeit.ifBlank { "-" },
            t("driver_lobby_arrived") to entry.uhrzeitAnmeldung.ifBlank { "-" }
        ),
        standortValue = standortLabel
    )
}

@Composable
private fun DriverInfoPanel(
    expanded: Boolean,
    onToggle: () -> Unit,
    rowOne: List<Pair<String, String>>,
    rowTwo: List<Pair<String, String>>,
    standortValue: String
) {
    Div({
        style {
            padding(6.px, 8.px)
            backgroundColor(Color.transparent)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(if (expanded) AlignItems.FlexEnd else AlignItems.Center)
                gap(8.px)
            }
        }) {
            Div({
                style {
                    flex(1)
                    minWidth(0.px)
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(6.px)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexWrap(FlexWrap.Wrap)
                        gap(6.px)
                        width(100.percent)
                    }
                }) {
                    rowOne.forEach { (title, value) ->
                        LumaticaInfoField(title, value)
                    }
                }

                if (expanded) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            flexWrap(FlexWrap.Wrap)
                            gap(6.px)
                            width(100.percent)
                        }
                    }) {
                        rowTwo.forEach { (title, value) ->
                            LumaticaInfoField(title, value)
                        }
                        StandortBlock(standortValue)
                    }
                }
            }

            Button(attrs = {
                onClick { onToggle() }
                style {
                    flexShrink(0)
                    width(28.px)
                    minWidth(28.px)
                    height(28.px)
                    padding(0.px)
                    backgroundColor(Color.transparent)
                    border(0.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                }
            }) {
                Img(
                    src = if (expanded) "images/up.svg" else "images/down.svg",
                    attrs = {
                        style {
                            width(18.px)
                            height(18.px)
                            property("filter", DashboardTheme.ICON_FILTER_WHITE)
                            opacity(0.9)
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun StandortBlock(value: String) {
    Div({
        style {
            width(100.percent)
            padding(6.px, 8.px)
            backgroundColor(Color.transparent)
            borderRadius(8.px)
            border(1.px, LineStyle.Solid, Color("rgba(255,255,255,0.06)"))
            boxSizing("border-box")
        }
    }) {
        Span({
            style {
                color(Color.white)
                fontSize(14.px)
                fontWeight("600")
                lineHeight("1.35")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
            }
        }) {
            Text(value.ifBlank { "-" })
        }
    }
}

private fun fahrerStatusLabel(entry: All): String {
    val isWe = entry.auftragsart.equals("WE", ignoreCase = true) || entry.lieferscheinnummer.isBlank()
    return when (entry.zustand) {
        ZustandManager.ANGELEGT -> LocalizationManager.get("driver_lobby_status_registered")
        ZustandManager.KONTROLLIERT -> LocalizationManager.get("driver_lobby_status_checked")
        ZustandManager.RESERVIERT -> LocalizationManager.get("driver_lobby_status_arrived")
        ZustandManager.TOR_ZUGEWIESEN -> LocalizationManager.get("driver_lobby_status_gate_assigned")
        ZustandManager.AM_TOR -> LocalizationManager.get("driver_lobby_status_at_gate")
        ZustandManager.PIN_ABFRAGE -> LocalizationManager.get("driver_lobby_status_pin_check")
        ZustandManager.IN_VERLADUNG -> if (isWe) LocalizationManager.get("driver_lobby_status_unloading") else LocalizationManager.get("driver_lobby_status_loading")
        ZustandManager.ABGESCHLOSSEN -> LocalizationManager.get("driver_completed")
        ZustandManager.STORNIERT -> LocalizationManager.get("driver_status_cancelled")
        else -> ZustandManager.textFor(entry.zustand)
    }
}

private fun sameLobbyStandort(
    entry: All,
    lobbyStandortId: String,
    fallbackStandort: String
): Boolean {
    val slotStandortId = fahrerLobbyStandortId(entry)
    return when {
        lobbyStandortId.isNotBlank() && slotStandortId.isNotBlank() -> slotStandortId == lobbyStandortId
        lobbyStandortId.isNotBlank() && entry.standort == lobbyStandortId -> true
        fallbackStandort.isNotBlank() -> entry.standort == fallbackStandort
        else -> true
    }
}

internal fun fahrerLobbyStandortId(entry: All): String =
    entry.slotStandortId.trim()
        .ifBlank { entry.waStandortId.trim() }
        .ifBlank { entry.standort.trim() }

private fun sameLobbyEntry(
    candidate: All,
    current: All
): Boolean {
    return when {
        candidate.id.isNotBlank() && candidate.id == current.id -> true
        candidate.lfdNr > 0 && candidate.lfdNr == current.lfdNr -> true
        else -> false
    }
}

private fun yardPrioValue(entry: All): Int? =
    when {
        entry.ablaufPrio > 0 -> entry.ablaufPrio
        else -> entry.prio.toIntOrNull()
    }

private fun lobbySammelKey(entry: All): String =
    if (entry.auftragsart.equals(SLOT_BEREICH_WE, ignoreCase = true) || entry.lieferscheinnummer.isBlank()) {
        entry.weSammelId.trim()
    } else {
        entry.waSammelPin
            .ifBlank { entry.waSammelId }
            .ifBlank { entry.pin }
            .trim()
    }

private fun lobbyAuftraege(entry: All, standortListe: List<All>): List<All> {
    val key = lobbySammelKey(entry)
    val source = if (key.isNotBlank()) {
        standortListe.filter { lobbySammelKey(it) == key }
    } else {
        listOf(entry)
    }
    return source.ifEmpty { listOf(entry) }
}

private fun lobbyLieferscheinnummern(entry: All, standortListe: List<All>): List<String> {
    return standortListe
        .map { it.lieferscheinnummer.trim() }
        .filter { it.isNotBlank() }
        .distinct()
        .ifEmpty { listOf(entry.lieferscheinnummer.trim()).filter { it.isNotBlank() } }
}

private fun sameLobbyGroup(candidate: All, current: All): Boolean {
    val currentKey = lobbySammelKey(current)
    val candidateKey = lobbySammelKey(candidate)
    return if (currentKey.isNotBlank() && candidateKey.isNotBlank()) {
        currentKey == candidateKey
    } else {
        sameLobbyEntry(candidate, current)
    }
}

private fun formatDurationWithSeconds(start: String, end: String): String {
    val startSeconds = parseClockSeconds(start) ?: return "-"
    val endSeconds = parseClockSeconds(end) ?: return "-"
    val diff = if (endSeconds >= startSeconds) {
        endSeconds - startSeconds
    } else {
        (24 * 60 * 60 - startSeconds) + endSeconds
    }
    val hours = diff / 3600
    val minutes = (diff % 3600) / 60
    val seconds = diff % 60
    return buildString {
        append(hours.toString().padStart(2, '0'))
        append(":")
        append(minutes.toString().padStart(2, '0'))
        append(":")
        append(seconds.toString().padStart(2, '0'))
    }
}

private fun parseClockSeconds(value: String): Int? {
    val parts = value.trim().split(":")
    if (parts.size < 2) return null
    val hours = parts[0].toIntOrNull() ?: return null
    val minutes = parts[1].toIntOrNull() ?: return null
    val seconds = parts.getOrNull(2)?.toIntOrNull() ?: 0
    return hours * 3600 + minutes * 60 + seconds
}

private fun currentClockTimeWithSeconds(): String {
    val now = js("new Date()")
    val hours = (now.getHours() as Int).toString().padStart(2, '0')
    val minutes = (now.getMinutes() as Int).toString().padStart(2, '0')
    val seconds = (now.getSeconds() as Int).toString().padStart(2, '0')
    return "$hours:$minutes:$seconds"
}

private fun buildStandortText(
    name: String,
    street: String,
    houseNumber: String,
    postalCode: String,
    city: String
): String {
    val addressLine = listOf(
        listOf(street, houseNumber).filter { it.isNotBlank() }.joinToString(" "),
        listOf(postalCode, city).filter { it.isNotBlank() }.joinToString(" ")
    ).filter { it.isNotBlank() }.joinToString(", ")

    return listOf(name, addressLine)
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .joinToString(" - ")
        .ifBlank { "-" }
}

